import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { isDemoMode, getDemoProfile } from '@/data/demoData';

export type UserType = 'creator' | 'sponsor';

export interface Profile {
  id: string;
  user_id: string;
  user_type: UserType;
  email: string;
  full_name: string;
  avatar_url?: string;
  bio?: string;
  display_name?: string;
  tagline?: string;
  location?: { country: string; city: string };
  social_handles?: any;
  social_platforms?: any;
  follower_counts?: any;
  niches?: string[];
  professional_level?: number;
  content_styles?: string[];
  collaboration_types?: string[];
  rate_range?: { min: number; max: number };
  preferred_sectors?: string[];
  avoided_sectors?: string[];
  availability_hours?: string;
  geographic_scope?: string;
  content_examples?: string[];
  onboarding_completed?: boolean;
  content_categories?: string[];
  engagement_rate?: number;
  company_name?: string;
  industry?: string;
  budget_range?: string;
  campaign_objectives?: string[];
  is_admin?: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, userType: UserType) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchProfile = async (userId: string) => {
    try {
      // Check if we're in demo mode
      if (isDemoMode()) {
        // Get demo profile based on current demo user type
        const demoUserType = localStorage.getItem('demo-user-type') as 'creator' | 'sponsor' || 'creator';
        const demoProfile = getDemoProfile(demoUserType);
        setProfile(demoProfile);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      setProfile(data ? {
        ...data,
        location: data.location as { country: string; city: string } | undefined,
        social_handles: data.social_handles as any,
        rate_range: data.rate_range as { min: number; max: number } | undefined
      } as Profile : null);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    // Check if we're in demo mode first
    if (isDemoMode()) {
      // In demo mode, create a fake user session
      const demoUserType = localStorage.getItem('demo-user-type') as 'creator' | 'sponsor' || 'creator';
      const demoProfile = getDemoProfile(demoUserType);
      
      // Create fake user object for demo
      const fakeUser = {
        id: demoProfile.user_id,
        email: demoProfile.email,
        created_at: demoProfile.created_at,
        updated_at: demoProfile.updated_at,
        app_metadata: {},
        user_metadata: {}
      } as any;
      
      setUser(fakeUser);
      setProfile(demoProfile);
      setLoading(false);
      return;
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Don't let Supabase overwrite demo mode state
        if (isDemoMode()) return;
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Defer profile fetch to avoid blocking auth state updates
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
        }

        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Don't let Supabase overwrite demo mode state
      if (isDemoMode()) return;
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // In demo mode, react to demo-user changes to swap profile AND user instantly (no reload)
  useEffect(() => {
    const handler = () => {
      if (isDemoMode()) {
        const demoUserType = localStorage.getItem('demo-user-type') as 'creator' | 'sponsor' || 'creator';
        const demoProfile = getDemoProfile(demoUserType);
        setProfile(demoProfile);
        setUser({
          id: demoProfile.user_id,
          email: demoProfile.email,
          created_at: demoProfile.created_at,
          updated_at: demoProfile.updated_at,
          app_metadata: {},
          user_metadata: {},
        } as any);
        setLoading(false);
      }
    };
    window.addEventListener('demo-user-changed' as any, handler as any);
    return () => window.removeEventListener('demo-user-changed' as any, handler as any);
  }, []);

  const signUp = async (email: string, password: string, fullName: string, userType: UserType) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          user_type: userType
        }
      }
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Registration Error",
        description: error.message
      });
    } else {
      toast({
        title: "Registration Successful",
        description: "Please check your email to confirm your account."
      });
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Login Error",
        description: error.message
      });
    }

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Logout Error",
        description: error.message
      });
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signUp,
      signIn,
      signOut,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}